import { faFileImport } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CryptoJS from 'crypto-js'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useState } from 'react'
import { BANNED_PROPS } from '@companion-app/shared/Expressions.js'
import type { ClientImportObject } from '@companion-app/shared/Model/ImportExport.js'
import { StaticAlert } from '~/Components/Alert.js'
import { Callout } from '~/Components/Callout.js'
import { ContextHelpButton } from '~/Layout/PanelIcons.js'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { base64EncodeUint8Array } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { ExportWizardModal } from './Export.js'
import { ImportWizard } from './Import/index.js'
import { ResetWizardModal } from './Reset.js'

const NOTIFICATION_ID_IMPORT = 'import_config_file'

export const ImportExportPage = observer(function ImportExport() {
	const { notifier, connections } = useContext(RootAppStoreContext)

	const [loadError, setLoadError] = useState<string | null>(null)

	const abortImportMutation = useMutationExt(trpc.importExport.abort.mutationOptions())

	const [importInfo, setImportInfo] = useState<[ClientImportObject, Record<string, string | undefined>] | null>(null)
	const clearImport = useCallback(() => {
		setImportInfo(null)

		abortImportMutation.mutateAsync().catch((e) => {
			console.error('Failed to abort import', e)
		})
	}, [abortImportMutation])

	const fileApiIsSupported = !!(window.File && window.FileReader && window.FileList && window.Blob)

	const startPrepareImportMutation = useMutationExt(trpc.importExport.prepareImport.start.mutationOptions())
	const cancelPrepareImportMutation = useMutationExt(trpc.importExport.prepareImport.cancel.mutationOptions())
	const uploadPrepareImportChunkMutation = useMutationExt(trpc.importExport.prepareImport.uploadChunk.mutationOptions())
	const completePrepareImportMutation = useMutationExt(trpc.importExport.prepareImport.complete.mutationOptions())

	const loadSnapshot = useCallback(
		(e: React.FormEvent<HTMLInputElement>) => {
			const newFile = e.currentTarget.files?.[0]
			e.currentTarget.value = null as any

			if (newFile === undefined || newFile.type === undefined) {
				setLoadError('Unable to read config file')
				return
			}

			setLoadError(null)
			notifier.show('Importing config...', 'This may take a while', null, NOTIFICATION_ID_IMPORT)
			console.log(`start import of ${newFile.size} bytes`)

			const hasher = CryptoJS.algo.SHA1.create()

			Promise.resolve()
				.then(async () => {
					const sessionId = await startPrepareImportMutation.mutateAsync({
						name: newFile.name,
						size: newFile.size,
					})
					if (!sessionId) throw new Error('Failed to start upload')

					let offset = 0
					await newFile
						.stream()
						.pipeTo(
							new WritableStream(
								{
									async write(chunk) {
										const chunkOffset = offset
										offset += chunk.length

										const success = await uploadPrepareImportChunkMutation.mutateAsync({
											sessionId,
											offset: chunkOffset,
											data: base64EncodeUint8Array(chunk),
										})
										if (!success) throw new Error(`Failed to upload chunk ${chunkOffset}`)

										hasher.update(CryptoJS.lib.WordArray.create(chunk))
									},
									async close() {
										console.log('uploading complete, starting load')
										const hashText = hasher.finalize().toString(CryptoJS.enc.Hex)

										const [err, config] = await completePrepareImportMutation.mutateAsync({
											sessionId,
											expectedChecksum: hashText,
											userData: null,
										})

										if (err || !config) {
											setLoadError(err || 'Failed to prepare')
										} else {
											const initialRemap: Record<string, string | undefined> = {}

											// Figure out some initial mappings. Look for matching type and hopefully label
											for (const [id, obj] of Object.entries(config.connections ?? {})) {
												if (!obj) continue

												const candidateIds = []
												let matchingLabelId = ''

												for (const [otherId, otherObj] of connections.connections.entries()) {
													if (otherObj.moduleId === obj.moduleId) {
														candidateIds.push(otherId)
														if (otherObj.label === obj.label) {
															matchingLabelId = otherId
														}
													}
												}

												if (BANNED_PROPS.has(id)) continue
												if (matchingLabelId) {
													initialRemap[id] = matchingLabelId
												} else {
													initialRemap[id] = candidateIds[0] || ''
												}
											}

											setLoadError(null)
											notifier.close(NOTIFICATION_ID_IMPORT)
											// const mode = config.type === 'page' ? 'import_page' : 'import_full'
											// modalRef.current.show(mode, config, initialRemap)
											setImportInfo([config, initialRemap])
										}
									},
								},
								{
									size: () => 1024 * 1024 * 1, // 1MB chunks
								}
							)
						)
						.catch((e) => {
							cancelPrepareImportMutation.mutateAsync({ sessionId }).catch((cancelErr) => {
								console.error('Failed to cancel import session', cancelErr)
							})
							throw e
						})
				})
				.catch((e) => {
					console.error('failed', e)

					notifier.show('Importing config...', 'Failed!', 5000, NOTIFICATION_ID_IMPORT)
				})
		},
		[
			startPrepareImportMutation,
			uploadPrepareImportChunkMutation,
			cancelPrepareImportMutation,
			completePrepareImportMutation,
			notifier,
			connections,
		]
	)

	if (importInfo) {
		return <ImportWizard importInfo={importInfo} clearImport={clearImport} />
	}

	// As of October 2025, this is only available on iOS Safari and iPadOS Safari
	const isMobileSafari = 'ongesturechange' in window

	return (
		<div>
			<h4 className="button-inline">
				Import / Export Configuration
				<ContextHelpButton action="/user-guide/config/import-export" />
			</h4>
			<p>On this page, you can import, export, and reset all settings stored in your Companion installation.</p>

			<Callout color="success">
				<h5>Export</h5>
				<p>Download a file containing all connections and button pages.</p>
				<ExportWizardModal />
			</Callout>

			<Callout color="warning">
				<h5>Import</h5>
				{!fileApiIsSupported ? (
					<>
						<StaticAlert color="warning">File uploading is not supported in your browser</StaticAlert>
					</>
				) : (
					<>
						<p>
							Use the button below to browse your computer for a <b>.companionconfig</b> file containing a configuration
							set.
						</p>

						<div>
							{loadError ? <StaticAlert color="warning">{loadError}</StaticAlert> : ''}

							<label className="button button-warning button-file">
								<FontAwesomeIcon icon={faFileImport} className="me-2" />
								Import configuration
								<input
									type="file"
									onChange={loadSnapshot}
									className="hidden"
									accept={isMobileSafari ? undefined : '.companionconfig,.yaml'} // Mobile safari doesn't support custom file extensions https://github.com/bitfocus/companion/issues/3676
								/>
							</label>
						</div>
					</>
				)}
			</Callout>

			<Callout color="danger">
				<h5>Reset</h5>
				<p>This will clear all connections, triggers and/or buttons.</p>
				<div>
					<ResetWizardModal />
				</div>
			</Callout>
		</div>
	)
})
